import { useState, FormEvent } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const FORM_URL = 'https://readdy.ai/api/form/d70id7r2m2156g4s6t2g';

const ContactPage = () => {
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const data = new URLSearchParams(new FormData(e.currentTarget) as unknown as Record<string, string>);
    setStatus('submitting');
    try {
      const res = await fetch(FORM_URL, { method: 'POST', headers: { 'Content-Type': 'application/x-www-form-urlencoded' }, body: data.toString() });
      setStatus(res.ok ? 'success' : 'error');
    } catch { setStatus('error'); }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <section className="bg-[#0C2340] py-20 nav-offset">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-[#C41E3A] text-sm font-bold uppercase tracking-widest mb-3">Contact</p>
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-5">Get in Touch</h1>
          <p className="text-xl text-white/75 max-w-2xl mx-auto">Whether you need service, have a partnership inquiry, or just have a question, we&apos;re here to help.</p>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <div>
              <h2 className="text-3xl font-bold text-[#111827] mb-8">Contact Information</h2>
              <div className="space-y-6 mb-10">
                {[
                  { icon: 'ri-phone-fill', title: 'Call Us', content: '(850) 669-8335', sub: '24/7 emergency service available', link: 'tel:+18506698335' },
                  { icon: 'ri-mail-fill', title: 'Email Us', content: 'hello@renovacomfort.com', sub: 'Typical response within 4 hours', link: 'mailto:hello@renovacomfort.com' },
                  { icon: 'ri-map-pin-fill', title: 'Our Address', content: '7901 4th St N # 33139', sub: 'St. Petersburg, FL 33702', link: null },
                  { icon: 'ri-time-fill', title: 'Business Hours', content: 'Mon–Fri: 7am – 8pm', sub: 'Sat 8am–6pm · Sun 9am–5pm · Emergency 24/7', link: null },
                ].map((c) => (
                  <div key={c.title} className="flex items-start space-x-5 p-5 bg-[#F8F9FB] rounded-xl border border-[#E2E8F0]">
                    <div className="w-12 h-12 bg-[#0C2340]/8 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className={`${c.icon} text-2xl text-[#0C2340]`} />
                    </div>
                    <div>
                      <h3 className="text-sm font-bold text-[#111827] mb-1">{c.title}</h3>
                      {c.link ? (
                        <a href={c.link} className="text-base font-semibold text-[#C41E3A] hover:text-[#A01830] transition-colors">{c.content}</a>
                      ) : (
                        <p className="text-base font-semibold text-[#111827]">{c.content}</p>
                      )}
                      <p className="text-xs text-[#6B7280] mt-1">{c.sub}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="bg-[#0C2340] rounded-xl p-7">
                <h3 className="text-white font-bold text-lg mb-3">For Partnerships &amp; Acquisitions</h3>
                <p className="text-white/60 text-sm mb-4">Interested in selling or partnering your home service business? Our M&amp;A team is actively looking for quality operators.</p>
                <a href="mailto:partnerships@renovacomfort.com" className="text-[#C41E3A] font-semibold text-sm hover:text-red-400 transition-colors">
                  partnerships@renovacomfort.com
                </a>
              </div>
            </div>

            <div>
              <h2 className="text-3xl font-bold text-[#111827] mb-6">Send a Message</h2>
              {status === 'success' ? (
                <div className="text-center py-14 bg-green-50 rounded-2xl border border-green-200">
                  <i className="ri-check-circle-fill text-5xl text-green-500 mb-4" />
                  <h3 className="text-xl font-bold text-[#111827]">Message Sent!</h3>
                  <p className="text-[#6B7280] mt-2">We&apos;ll get back to you within one business day.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} data-readdy-form="true" id="contact-form" className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-[#111827] mb-2">First Name *</label>
                      <input name="first_name" required className="w-full px-4 py-3 rounded-lg border border-[#E2E8F0] focus:ring-2 focus:ring-[#0C2340] outline-none text-sm" placeholder="Jane" />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-[#111827] mb-2">Last Name *</label>
                      <input name="last_name" required className="w-full px-4 py-3 rounded-lg border border-[#E2E8F0] focus:ring-2 focus:ring-[#0C2340] outline-none text-sm" placeholder="Smith" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-[#111827] mb-2">Email Address *</label>
                    <input name="email" type="email" required className="w-full px-4 py-3 rounded-lg border border-[#E2E8F0] focus:ring-2 focus:ring-[#0C2340] outline-none text-sm" placeholder="jane@example.com" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-[#111827] mb-2">Phone Number</label>
                    <input name="phone" type="tel" className="w-full px-4 py-3 rounded-lg border border-[#E2E8F0] focus:ring-2 focus:ring-[#0C2340] outline-none text-sm" placeholder="(555) 123-4567" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-[#111827] mb-2">Inquiry Type</label>
                    <select name="inquiry_type" className="w-full px-4 py-3 rounded-lg border border-[#E2E8F0] focus:ring-2 focus:ring-[#0C2340] outline-none text-sm">
                      <option>General Inquiry</option>
                      <option>Service Request</option>
                      <option>Commercial Services</option>
                      <option>Partnership / Acquisition</option>
                      <option>Careers</option>
                      <option>Media / Press</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-[#111827] mb-2">Message *</label>
                    <textarea name="message" required rows={5} maxLength={500} onChange={(e) => setCharCount(e.target.value.length)} className="w-full px-4 py-3 rounded-lg border border-[#E2E8F0] focus:ring-2 focus:ring-[#0C2340] outline-none text-sm resize-none" placeholder="How can we help?" />
                    <p className="text-xs text-[#9CA3AF] mt-1">{charCount}/500</p>
                  </div>
                  {status === 'error' && <p className="text-sm text-red-600">Something went wrong. Please try again.</p>}
                  <button type="submit" disabled={status === 'submitting'} className="w-full bg-[#C41E3A] text-white py-4 rounded-lg text-sm font-semibold hover:bg-[#A01830] transition-colors disabled:opacity-50 whitespace-nowrap cursor-pointer">
                    {status === 'submitting' ? 'Sending...' : 'Send Message'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default ContactPage;
